import { useState, useMemo, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Typography, InputAdornment } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS, buildApiUrl } from '@/constants';
import { api } from '@/utils';
import useFetchCampaignsForCalls from './useFetchCampaignsForCalls';

import type { Campaign } from '@/components/campaigns/types';

interface CampaignSelectorProps {
    selectedCampaignId: string | null;
    onCampaignChange: (campaignId: string | null) => void;
}

const INITIAL_CAMPAIGNS_LIMIT = 25;
const CAMPAIGNS_SEARCH_LIMIT = 100;

/**
 * Campaign selector autocomplete component.
 *
 * - Initially displays a predefined number of campaigns.
 * - Triggers a backend search when a keyword is entered and the Enter key is pressed.
 */
const CampaignSelector: React.FC<CampaignSelectorProps> = ({ selectedCampaignId, onCampaignChange }) => {
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const getProjectLabel = (campaign: Campaign): string => campaign.name || `Project ${campaign.id}`;

    const searchParams = useMemo(() => {
        // Only search when user has triggered a search (pressed Enter)
        if (isSearching && searchTerm && searchTerm.trim().length >= 3) {
            return { search: searchTerm.trim(), limit: CAMPAIGNS_SEARCH_LIMIT };
        }

        // Otherwise show the initial number of campaigns
        return { limit: INITIAL_CAMPAIGNS_LIMIT };
    }, [searchTerm, isSearching]);

    const { data: campaigns, isLoading: isLoadingCampaigns, isError } = useFetchCampaignsForCalls(searchParams);

    // Fetch the selected campaign by ID (when loading the project from the URL or localStorage)
    const { data: fetchedSelectedCampaign, isLoading: isLoadingSelected } = useQuery<Campaign>({
        queryKey: ['campaign-by-id', selectedCampaignId],
        queryFn: async () => {
            const url = buildApiUrl(API_ENDPOINTS.CAMPAIGN_BY_ID(selectedCampaignId!));
            const response = await api.get(url);
            return response.data;
        },
        enabled: !!selectedCampaignId,
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const isLoading = isLoadingCampaigns || (!!selectedCampaignId && isLoadingSelected);

    // Find the selected campaign object from the ID (check fetched campaign first, then list)
    const selectedCampaign = useMemo(() => {
        if (!selectedCampaignId) return null;

        if (fetchedSelectedCampaign && fetchedSelectedCampaign.id.toString() === selectedCampaignId) {
            return fetchedSelectedCampaign;
        }

        if (campaigns) {
            return campaigns.find((campaign) => campaign.id.toString() === selectedCampaignId) || null;
        }

        return null;
    }, [selectedCampaignId, campaigns, fetchedSelectedCampaign]);

    // Sync input value when selected campaign changes (e.g., on initial load from URL)
    useEffect(() => {
        if (!isSearching) {
            setInputValue(selectedCampaign ? getProjectLabel(selectedCampaign) : '');
        }
    }, [selectedCampaign?.id, isSearching]);

    // Ensure selected campaign is always in the options list to prevent toggling
    const optionsWithSelected = useMemo(() => {
        if (!campaigns) return [];

        if (selectedCampaign && !campaigns.some((c) => c.id === selectedCampaign.id)) {
            return [selectedCampaign, ...campaigns];
        }

        return campaigns;
    }, [campaigns, selectedCampaign]);

    const handleChange = (_event: React.SyntheticEvent, newValue: Campaign | null, reason: string) => {
        onCampaignChange(newValue ? newValue.id.toString() : null);

        if (reason === 'selectOption' && newValue) {
            // Show selected campaign name but don't trigger search
            setIsSearching(false);
            setInputValue(getProjectLabel(newValue));
            setSearchTerm('');
            setIsOpen(false);
        } else if (reason === 'clear') {
            setIsSearching(false);
            setInputValue('');
            setSearchTerm('');
            setIsOpen(false);
        }
    };

    const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
        if (reason === 'input') {
            setInputValue(newInputValue);

            // Clear search if input is empty
            if (newInputValue.trim().length === 0) {
                setIsSearching(false);
                setSearchTerm('');
            }
        } else if (reason === 'clear') {
            setIsSearching(false);
            setInputValue('');
            setSearchTerm('');
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            const trimmedValue = inputValue.trim();

            if (trimmedValue.length >= 3) {
                setIsSearching(true);
                setSearchTerm(trimmedValue);
            } else if (trimmedValue.length === 0) {
                setIsSearching(false);
                setSearchTerm('');
            }
        }
    };

    return (
        <Autocomplete
            id="campaign-selector"
            options={optionsWithSelected}
            value={selectedCampaign}
            inputValue={inputValue}
            open={isOpen && (!selectedCampaign || isSearching)}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            onChange={handleChange}
            onInputChange={handleInputChange}
            loading={isLoading}
            disabled={isError}
            popupIcon={selectedCampaign && !isSearching ? null : undefined}
            getOptionLabel={getProjectLabel}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterOptions={(options) => options} // No client-side filtering, handled by backend
            renderOption={(props, option) => {
                const { key, ...restProps } = props;

                return (
                    <Box component="li" key={key} {...restProps}>
                        <Typography noWrap>{getProjectLabel(option)}</Typography>
                    </Box>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder="Search projects..."
                    size="small"
                    error={isError}
                    helperText={isError ? 'Error loading projects' : undefined}
                    onKeyDown={handleKeyDown}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start" sx={{ ml: 0.5, mr: 0 }}>
                                    <Search fontSize="small" sx={{ color: 'devblue.60' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <>
                                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            noOptionsText={
                inputValue.length >= 3
                    ? 'No projects found. Press Enter to search.'
                    : 'Type at least 3 characters and press Enter to search...'
            }
            slotProps={{
                listbox: {
                    style: { maxHeight: '400px', padding: 0 },
                },
                paper: {
                    style: {
                        maxWidth: 600,
                    },
                },
            }}
            sx={{ minWidth: '450px' }}
        />
    );
};

export default CampaignSelector;
